import City from '../../model/political/city';
import Territory from '../../model/political/territory';
import Debug from 'debug';
const debug = Debug('lib.strings.city');

/**
 * A town is read, not announced.
 *
 * The name and the territory are typography (see pages/City.style.js) rather than a sentence.
 * Everything else the player needs is carried by what is physically in front of them: nobody
 * is told the town is suspicious, they are shown a curtain moving. The mechanic is learned by
 * crossing enough towns to recognise the vocabulary.
 *
 * Lines are picked off a hash of the town name, not at random, so a place says the same thing
 * every time the player walks into it. Somewhere that re-describes itself is not a place.
 */

const attitudes = {
  [Territory.Attitude.Hostile]: [
    'a door shuts. then another.',
    'a man in the road does not move aside.',
    'someone spits in the dust.',
    'they hold their doorways and wait for you to pass.'
  ],
  [Territory.Attitude.Suspicious]: [
    'they watch from the doorways and say nothing.',
    'a curtain moves and settles.',
    'talk stops when you pass and starts again behind you.',
    'a woman takes a child by the arm and goes inside.'
  ],
  [Territory.Attitude.Neutral]: [
    'they look up and go back to what they were doing.',
    'a man sweeping the boards does not break his stroke.',
    'nobody stops. nobody stares.',
    'the town has seen worse come through.'
  ],
  [Territory.Attitude.Friendly]: [
    'a woman fills a cup at the pump and hands it over.',
    'an old man moves along the bench to make room.',
    'a door stands open. nobody asks anything.',
    'someone points you at the shade and goes back to work.'
  ]
};

/**
 * The first town is not read the way the rest are.
 *
 * The player crawls into it half dead and drinks out of a dog's bowl in the dirt
 * (model/events/story/enter.js). A friendly roll would put a woman at the pump handing over
 * a cup a beat before that modal opens, and a hostile one would have the town square up to a
 * man who cannot stand — either way the street is answering him, and nobody comes. So the
 * first town does not read its territory's attitude at all. He arrives on his own and is not
 * seen, and the water he finds is the water a dog left.
 */
const unnoticed = [
  'nobody comes out to see what fell in the road.',
  'a man passes with a bucket and does not turn his head.',
  'the town goes on with what it was doing.',
  'no call, no question. the street as it was.'
];

const generosities = {
  [City.Generosity.Greedy]: [
    'the scales are shaved and everyone knows it.',
    'he names a price and then names it again, higher.',
    'nothing here is given. all of it is sold.'
  ],
  [City.Generosity.Fair]: [
    'the scales are honest.',
    'a price is named once and it holds.',
    'he counts it out and neither of you speaks.'
  ],
  [City.Generosity.Generous]: [
    'bread comes across the counter before the coin does.',
    'they weigh light and call it even.',
    'he waves off the last of it.'
  ]
};

const standings = {
  [City.StatusInTown.Fringe]: 'nobody here knows your name.',
  [City.StatusInTown.Familiar]: 'a man raises a hand. he has seen you before.',
  [City.StatusInTown.Dominant]: 'they step aside in the road. they know what you are.'
};

/**
 * The same three standings at the moment they turn over, rather than as read off the street on
 * arrival. Beg, rob and work all cross these lines — political.updateStatusMetrics reports the
 * crossing to whichever action caused it — so the wording lives here once. Three copies of
 * `town now pays you taxes` used to sit in the three action files.
 *
 * What actually changes is passiveControl.js: Familiar pays 5 of each resource a day, Dominant
 * pays 50, Fringe pays nothing. So Familiar is something small left where you sleep, Dominant
 * is tribute carried to you, and Fringe is that stopping — the same step, empty.
 */
const standingChanges = {
  [City.StatusInTown.Fringe]: 'nothing on the step in the morning. that is over now.',
  [City.StatusInTown.Familiar]: 'bread on the step at first light. nobody waits to be thanked.',
  [City.StatusInTown.Dominant]: 'they bring what they owe and do not wait to be asked.'
};

const standingChanged = status => standingChanges[status] || null;

// djb2. Enough to spread a dozen town names across four lines.
const hash = text => {
  let value = 5381;
  for (let i = 0; i < text.length; i++) {
    value = (value * 33) ^ text.charCodeAt(i);
  }
  return Math.abs(value);
};

const pick = (list, seed) => {
  if (!list || list.length === 0) return null;
  return list[hash(seed) % list.length];
};

/**
 * @returns {{ name: string, territory: string, lines: string[] }}
 */
const getArrival = ({
  name,
  territory,
  attitude,
  generosity,
  standing,
  showGenerosity,
  showStanding,
  firstTown
}) => {
  debug('arrival', name, 'attitude', attitude, 'generosity', generosity, 'standing', standing);

  // Attitude is what is in front of the player the moment they walk in, so it is always here.
  // The first town is indifferent by rule — see `unnoticed`.
  const lines = [firstTown ? pick(unnoticed, name) : pick(attitudes[attitude], name)];

  // The scales say nothing until there is a counter to stand at. Held back until trade opens.
  if (showGenerosity) lines.push(pick(generosities[generosity], `${name}.trade`));

  // Standing is the last thing to appear. The town readout grows the way everything else does.
  if (showStanding && standings[standing]) lines.push(standings[standing]);

  return { name, territory, lines: lines.filter(Boolean) };
};

export { getArrival, standingChanged };
